import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Users,
  Workflow,
  FileText,
  FolderTree,
  Sparkles,
  Clock,
  ArrowRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  IndustryPreset,
  AgentPresetSummary,
  WorkflowPresetSummary,
  TemplatePresetSummary,
} from '@/lib/api/services/industryPresets'

interface PresetPreviewDialogProps {
  preset: IndustryPreset | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onApply: (preset: IndustryPreset, includeDefaultAgents: boolean) => void
  isApplying?: boolean
}

export function PresetPreviewDialog({
  preset,
  open,
  onOpenChange,
  onApply,
  isApplying = false,
}: PresetPreviewDialogProps) {
  const [includeDefaultAgents, setIncludeDefaultAgents] = useState(true)

  if (!preset) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${preset.color}15` }}
            >
              {getIndustryIcon(preset.icon)}
            </div>
            <div>
              <DialogTitle className="text-xl">{preset.name}</DialogTitle>
              <DialogDescription className="mt-1">
                {preset.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-3 py-4 border-y border-gray-100 dark:border-gray-800">
          <StatBadge icon={<Users />} value={preset.agents.length} label="Agents" />
          <StatBadge icon={<Workflow />} value={preset.workflows.length} label="Workflows" />
          <StatBadge icon={<FileText />} value={preset.templates.length} label="Templates" />
          <StatBadge icon={<Clock />} value={`${preset.estimatedSetupMinutes}m`} label="Setup" />
        </div>

        {/* Tabs Content */}
        <Tabs defaultValue="agents" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="agents">Agents</TabsTrigger>
            <TabsTrigger value="workflows">Workflows</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="folders">Folders</TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 mt-4">
            <TabsContent value="agents" className="mt-0 space-y-3">
              {preset.agents.map((agent) => (
                <AgentCard key={agent.slug} agent={agent} />
              ))}
            </TabsContent>

            <TabsContent value="workflows" className="mt-0 space-y-3">
              {preset.workflows.map((workflow) => (
                <WorkflowCard key={workflow.name} workflow={workflow} />
              ))}
            </TabsContent>

            <TabsContent value="templates" className="mt-0 space-y-3">
              {preset.templates.map((template) => (
                <TemplateCard key={template.name} template={template} />
              ))}
            </TabsContent>

            <TabsContent value="folders" className="mt-0">
              <div className="space-y-2">
                {preset.folderStructure.map((folder) => (
                  <FolderItem key={folder.path} folder={folder} />
                ))}
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <Separator />

        {/* Footer */}
        <div className="flex items-center justify-between pt-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={includeDefaultAgents}
              onCheckedChange={(checked) => setIncludeDefaultAgents(checked === true)}
            />
            <span>Include 6 default Templatean agents</span>
          </label>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => onApply(preset, includeDefaultAgents)}
              disabled={isApplying}
              style={{ backgroundColor: preset.color }}
            >
              {isApplying ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
                  Applying...
                </>
              ) : (
                <>
                  Apply This Preset
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function StatBadge({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode
  value: number | string
  label: string
}) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1 text-muted-foreground">
        <span className="h-4 w-4">{icon}</span>
        <span className="font-semibold text-foreground">{value}</span>
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}

function AgentCard({ agent }: { agent: AgentPresetSummary }) {
  return (
    <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <div className="flex items-start gap-3">
        <span
          className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
          style={{ backgroundColor: `${agent.color}15` }}
        >
          {agent.emoji}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{agent.name}</span>
            <Badge variant="secondary" className="text-xs">
              {agent.skillCount} skills
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {agent.description}
          </p>
          {agent.personality && (
            <p className="text-xs text-muted-foreground mt-1 italic">
              "{agent.personality}"
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function WorkflowCard({ workflow }: { workflow: WorkflowPresetSummary }) {
  return (
    <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <div className="flex items-start gap-3">
        <Workflow className="h-5 w-5 text-blue-500 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{workflow.name}</span>
            <Badge variant="secondary" className="text-xs">
              {workflow.stepCount} steps
            </Badge>
            {workflow.category && (
              <Badge variant="outline" className="text-xs">
                {workflow.category}
              </Badge>
            )}
          </div>
          {workflow.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {workflow.description}
            </p>
          )}
          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
            <span>Trigger: {workflow.triggerType}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function TemplateCard({ template }: { template: TemplatePresetSummary }) {
  const typeColors: Record<string, string> = {
    Word: 'text-blue-500',
    Excel: 'text-green-500',
    PowerPoint: 'text-orange-500',
    Email: 'text-purple-500',
  }

  return (
    <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <div className="flex items-start gap-3">
        <FileText className={cn('h-5 w-5 mt-0.5', typeColors[template.templateType] || 'text-gray-500')} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{template.name}</span>
            <Badge variant="secondary" className="text-xs">
              {template.templateType}
            </Badge>
            {template.category && (
              <Badge variant="outline" className="text-xs">
                {template.category}
              </Badge>
            )}
          </div>
          {template.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {template.description}
            </p>
          )}
          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
            <span>{template.variableCount} variables</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function FolderItem({ folder }: { folder: { name: string; path: string; childCount: number } }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <FolderTree className="h-4 w-4 text-amber-500" />
      <span className="text-sm font-medium">{folder.name}</span>
      <span className="text-xs text-muted-foreground">{folder.path}</span>
      {folder.childCount > 0 && (
        <Badge variant="secondary" className="text-xs ml-auto">
          {folder.childCount} sub-folders
        </Badge>
      )}
    </div>
  )
}

function getIndustryIcon(iconName: string): string {
  const icons: Record<string, string> = {
    scale: '\u2696\ufe0f',
    'chart-line': '\ud83d\udcc8',
    heartbeat: '\ud83c\udfe5',
    cogs: '\u2699\ufe0f',
    megaphone: '\ud83d\udce2',
    home: '\ud83c\udfe0',
    'graduation-cap': '\ud83c\udf93',
    briefcase: '\ud83d\udcbc',
    industry: '\ud83c\udfed',
  }
  return icons[iconName] || '\ud83d\udcc1'
}

export default PresetPreviewDialog
